import { find, get } from 'lodash-es';

import * as angular from 'angular';

import * as moment from 'moment';

interface IconData {
  contracted?: boolean;
  contractPurchasePlanningMode;
  contractMarketShare;
  contractEffectiveDate: number;
}

/**
 * Today, we only use this with OM-1 flag on, but ultimately we need to use this everywhere
 */
class MicroChartWithTiersCtrl {
  static bindings = {
    microChartData: '<',
    iconData: '<?',
    targetMarketShare: '<',
    marketShare: '<',
    showModal: '<',
    size: '<',
    zoom: '&?',
    download: '<'
  };

  microChartData;
  iconData: IconData;
  targetMarketShare;
  marketShare;
  showModal;
  size;
  zoom;
  download;

  graphData; // the data we pass to the highcharts directive
  underTarget = false;
  zoomOverlay;
  icon;
  hover;
  currentMarketShare;
  target;

  constructor(
    private $attrs,
    private TierMicroChartService,
    private MicroChartWithTiersModal
  ) {
    'ngInject';
  }

  $onInit() {
    this.zoomOverlay = angular.isDefined(this.$attrs.zoom) || this.showModal;
  }

  // NOTE: We currently don't support the size attribute changing ... because that never happens currently.
  // But it shouldn't be hard to add if needed.
  $onChanges(changes) {
    // console.log('Changes object:');
    // console.log(changes);
    // console.log('Properties of this');
    // console.log(this.iconData);
    // console.log(this.microChartData);
    // console.log(this.targetMarketShare);
    // console.log(this.marketShare);
    // console.log('Properties of changes');
    // console.log(changes.iconData.currentValue);
    // console.log(changes.microChartData.currentValue);
    // console.log(changes.targetMarketShare.currentValue);
    // console.log(changes.marketShare.currentValue);

    if (get(changes.iconData, 'currentValue')) {
      this.updateIconData(changes.iconData.currentValue);
    }

    if (
      get(changes.microChartData, 'currentValue') ||
      get(changes.targetMarketShare, 'currentValue') != null ||
      get(changes.marketShare, 'currentValue') != null
    ) {
      let microChartData = this.microChartData;
      let targetMarketShare = this.targetMarketShare;
      let marketShare = this.marketShare;

      // console.log('Inside the Microchart component with these values:');

      // Make sure we have microchart data (market share and target market share are optional fields)
      if (microChartData != null) {
        // Generate the highcharts graph data from the micro chart data

        if (this.size == 'modal' || this.size == 'large') {
          this.graphData = this.TierMicroChartService.generateMacroChart(
            microChartData,
            targetMarketShare,
            marketShare
          );
        } else {
          this.graphData = this.TierMicroChartService.generateMicroChart(
            microChartData,
            targetMarketShare,
            marketShare
          );
        }

        // console.log(this.graphData);

        if (get(this.graphData, 'series') != null) {
          // TODO: what are we actually doing, updating the target? Rename this to say so
          this.updateMicroChartData(this.graphData);
        }
      }
    }
  }

  updateIconData(iconData: IconData) {
    // Figure out if this one is contracted and if it is, make sure we've got the icon and hover for it.
    if (iconData.contracted) {
      this.icon = '/aptitude/images/components/micro-chart/contracted.svg';
      this.hover = `<table class="hover">
                      <tr><td class="key">Status</td><td class="value">Contracted</td></tr>
                      ${this.type(iconData.contractPurchasePlanningMode)}
                      <tr><td class="key">Commit</td><td class="value">${
                        iconData.contractMarketShare
                      }%</td></tr>
                      <tr><td class="key">Date</td><td class="value">${moment(
                        iconData.contractEffectiveDate
                      ).format('L')}</td></tr>
                    </table>`;
    }
  }

  updateMicroChartData(graphData) {
    // console.log('Series exists, so updating the microchart data');
    // console.log(graphData);

    // Dig into the data to get the current market share.
    let allocationSeries: any = find(graphData.series, {
      id: 'currentAllocationText'
    });

    if (allocationSeries) {
      this.currentMarketShare = (allocationSeries as any).dataLabels.format;
    }

    // And figure out if we're under target.
    this.target = get(graphData, 'sourceData.targetMarketShare', 0);

    // TIL about AngularJS that setting ng-src to null or '' does _not_ clear out any existing image. They blow it
    // off and do so intentionally: https://github.com/angular/angular.js/issues/1218
    //
    // So now the image for under target is always there, but it is shown/hidden based upon whether it actually is
    // under target or not.
    this.underTarget = this.currentMarketShare < this.target; // This is horrible that we had to do this. TODO: Use tiers to figure this out
  }

  private typeToString(type) {
    switch (type) {
      case 'GROUP_PLAN':
        return 'Group Plan';
      case 'DUAL_SOURCE':
        return 'Dual Prime Vendor';
      case 'INDIVIDUAL_COMMITMENT':
        return 'Individual Commit';
    }
  }

  private type(mode) {
    if (mode) {
      return `<tr><td class="key">Type</td><td class="value">${this.typeToString(
        mode
      )}</td></tr>`;
    } else {
      return ``;
    }
  }

  /**
   * This gets called by the microchart component when the user clicks the microchart to zoom it
   */
  doZoom() {
    if (this.showModal) {
      this.MicroChartWithTiersModal.openModal(
        this.microChartData,
        this.iconData,
        this.targetMarketShare,
        this.marketShare,
        this.download
      );
    } else {
      // Call the zoom handler that was passed in
      this.zoom();
    }
  }
}

export const MicroChartWithTiersComponent = {
  bindings: MicroChartWithTiersCtrl.bindings,
  controller: MicroChartWithTiersCtrl,
  template: `<div data-test-selector="mini-chart-current-market-share"
                  class="minichartLabel"
                  data-trigger="hover"
                  data-auto-close="true"
                  data-placement="bottom"
                  data-html="true"
                  data-content-template="popover.html"
                  bs-popover
                  ng-class="{ underTarget: $ctrl.underTarget }"
                  ng-if="$ctrl.currentMarketShare && $ctrl.zoomOverlay">
               {{ $ctrl.currentMarketShare }}%
               <img src="/aptitude/images/components/micro-chart/target.svg" ng-hide="!$ctrl.underTarget">
             </div>

             <div class="microChart" aptitude-micro-chart="$ctrl.graphData" zoom="$ctrl.doZoom()" icon="$ctrl.icon"
                  hover="$ctrl.hover" ng-if="$ctrl.graphData && $ctrl.zoomOverlay"></div>

             <div data-test-selector="mini-chart-current-market-share"
                  class="minichartLabel"
                  ng-class="{ underTarget: $ctrl.underTarget }"
                  ng-if="$ctrl.currentMarketShare && !$ctrl.zoomOverlay">
               {{ $ctrl.currentMarketShare }}%
               <img src="/aptitude/images/components/micro-chart/target.svg" ng-hide="!$ctrl.underTarget">
             </div>

             <div class="microChart" aptitude-micro-chart="$ctrl.graphData" icon="$ctrl.icon"
                  hover="$ctrl.hover" ng-if="$ctrl.graphData && !$ctrl.zoomOverlay"></div>
             <script type="text/ng-template" id="popover.html">
               <table class="hover">
                 <tr ng-if="$ctrl.underTarget">
                   <td class="key">Status</td><td class="value">Under Target</td>
                 </tr>
                 <tr><td class="key">Target</td><td class="value">{{ $ctrl.target }}%</td></tr>
                 <tr>
                   <td class="key">Allocated</td>
                   <td class="value" ng-class="{ underTarget: $ctrl.underTarget }">{{ $ctrl.currentMarketShare }}%</td>
                 </tr>
               </table>
             </script>`
};
